import { useRef, useCallback, useState, type ReactNode, type MouseEvent } from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface HueGlowCardProps {
  hueColor: string;
  className?: string;
  children: ReactNode;
}

/**
 * A frosted-glass card with a radial hue glow that follows the mouse cursor.
 * The glow appears on hover and fades out on mouse leave — inspired by Fey.com.
 */
export function HueGlowCard({ hueColor, className, children }: HueGlowCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current || !glowRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    glowRef.current.style.background = `radial-gradient(300px circle at ${String(x)}px ${String(y)}px, ${hueColor}20, transparent 70%)`;
  }, [hueColor]);

  const handleMouseEnter = useCallback(() => { setIsHovering(true); }, []);
  const handleMouseLeave = useCallback(() => { setIsHovering(false); }, []);

  return (
    <Card
      ref={cardRef}
      className={cn("glass-card relative overflow-hidden", className)}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Mouse-tracking glow layer */}
      <div
        ref={glowRef}
        className="pointer-events-none absolute inset-0 z-0 transition-opacity duration-300"
        style={{ opacity: isHovering ? 1 : 0 }}
      />
      {/* Content layer */}
      <div className="relative z-10">
        {children}
      </div>
    </Card>
  );
}
